import { Bus, LocateFixed, MapPin, Navigation, TrainFront } from 'lucide-react';
import type { Journey, NextUp } from '@tempo/core';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { MODE_LABEL, dayName, duration, relative, time } from '../lib/format';
import { sendLocation, useNow } from '../lib/hooks';
import { toast } from '../lib/toast';
import { ModeIcon } from './kinds';
import { Button } from './ui';

export function NextUpCard({ next }: { next: NextUp }) {
  const now = useNow(15_000);
  const qc = useQueryClient();
  const [locating, setLocating] = useState(false);
  const { item, travel, live } = next;
  if (!item) {
    return (
      <div className="nextup">
        <div className="eyebrow">Up next</div>
        <div className="what">Nothing else planned</div>
        <p className="subtle small" style={{ marginTop: 6 }}>
          Enjoy the free time – or add something with +.
        </p>
      </div>
    );
  }

  // Most accurate first: the actual bus/train → live estimate from your location → the plan.
  const journey = next.journey;
  const leaveAt = journey?.leaveAt ?? live?.leaveAt ?? next.leaveAt;
  const travelMin = journey?.minutes ?? live?.travelMin ?? next.travelMin;
  const mins = leaveAt ? Math.round((leaveAt - now) / 60_000) : null;
  const cls = mins == null ? '' : mins < 0 ? 'late' : mins <= 10 ? 'urgent' : '';
  const inProgress = item.start <= now;

  const locate = async () => {
    setLocating(true);
    try {
      const r = await sendLocation();
      qc.setQueryData(['next'], r);
      toast(r.live?.arrived ? "You're there 👍" : 'Updated from your location');
    } catch (err) {
      toast((err as Error).message, 'error');
    } finally {
      setLocating(false);
    }
  };

  return (
    <div className="nextup">
      <div className="eyebrow">{inProgress ? 'Now' : 'Up next'}</div>
      <div className="what">{item.title}</div>
      <div className="subtle small row wrap" style={{ gap: 6, marginTop: 4 }}>
        <span>
          {dayName(item.start) === 'Today' ? '' : `${dayName(item.start)} `}
          {time(item.start)}–{time(item.end)} · {relative(item.start, now)}
        </span>
        {item.location && (
          <span className="row" style={{ gap: 4 }}>
            <MapPin size={13} /> {item.location}
          </span>
        )}
      </div>

      {live?.arrived ? (
        <div className="leave">
          <span className="countdown" style={{ color: 'var(--ok)' }}>
            You're there
          </span>
        </div>
      ) : leaveAt ? (
        <div className="leave">
          <span className={`countdown ${cls}`}>{mins! < 0 ? `Leave now` : mins === 0 ? 'Leave now' : `Leave in ${duration(mins! * 60_000)}`}</span>
          <span className="subtle row" style={{ gap: 6 }}>
            <ModeIcon mode={next.mode} /> by {time(leaveAt)} · {travelMin} min {MODE_LABEL[next.mode ?? ''] ?? ''}
            {live?.fromLocation && live.travelMin != null ? ' from where you are' : ''}
          </span>
        </div>
      ) : null}
      {journey && journey.steps.some((s) => s.mode === 'transit') && <JourneySteps journey={journey} />}
      {live && live.leaveAt && next.leaveAt && Math.abs(live.leaveAt - next.leaveAt) > 5 * 60_000 && (
        <p className="tiny subtle" style={{ marginTop: 6 }}>
          Planned departure was {time(next.leaveAt)} – adjusted for your current location.
        </p>
      )}

      <div className="actions">
        {next.directionsUrl && (
          <a className="btn primary" href={next.directionsUrl} target="_blank" rel="noreferrer">
            <Navigation size={16} /> Directions
          </a>
        )}
        {(travel || item.placeId) && (
          <Button onClick={locate} loading={locating}>
            <LocateFixed size={16} /> {live ? 'Update location' : 'Use my location'}
          </Button>
        )}
      </div>
    </div>
  );
}

/** Walk 5 min → U1C 16:32 Portswood Broadway → Above Bar Street (8 stops) → walk 4 min */
export function JourneySteps({ journey }: { journey: Journey }) {
  return (
    <div className="journey">
      {journey.steps.map((s, i) =>
        s.mode === 'transit' ? (
          <div key={i} className="step transit">
            <span className="line">
              {/train|rail|subway/i.test(s.vehicle ?? '') ? <TrainFront size={14} /> : <Bus size={14} />} {s.line ?? s.vehicle ?? 'Bus'}
            </span>
            <span>
              {s.departAt ? <b>{time(s.departAt)}</b> : null} {s.from}
              {s.to ? ` → ${s.to}` : ''}
              {s.stops ? <span className="faint"> · {s.stops} stops</span> : null}
            </span>
          </div>
        ) : (
          <div key={i} className="step">
            <ModeIcon mode={s.mode} size={14} /> {s.minutes} min
          </div>
        ),
      )}
    </div>
  );
}
